/**
 * Dealing the icebreaker deck (D62).
 *
 * Here rather than in the component because the deck is rendered in three
 * places now — its own route, the Schedule's desktop rail, and the Schedule's
 * phone switcher — and the order cards come out in is the one thing all three
 * have to agree about. A guest who swipes to card four in the rail and then
 * opens the route should not meet a different card four.
 */
import type { IcebreakerCard } from './content';

export const SEED_KEY = 'mv.deck';

/** A seeded PRNG, so a deal is reproducible from its seed alone. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Would the card at `i` sit happily between its neighbours? */
const settled = (arr: IcebreakerCard[], i: number): boolean =>
  (i <= 0 || arr[i].tag !== arr[i - 1].tag)
  && (i >= arr.length - 1 || arr[i].tag !== arr[i + 1].tag);

/**
 * Break up runs of the same tag, in place.
 *
 * **Swaps with anywhere, not just forwards.** The first version scanned ahead
 * for a card to trade with, which cannot fix a clash near the end of the deck —
 * there is nothing left to trade with — so a deck whose commonest tag happened
 * to land late finished `food, food, food` and the pass reported success. The
 * test that caught it runs 200 seeds rather than one, because a single seed
 * that comes out clean proves nothing about the pass.
 *
 * Each round fixes the first clash and then rescans from the start, since a
 * swap can introduce one behind it. Bounded by the number of cards, and it
 * gives up rather than spinning when the multiset genuinely cannot be
 * separated — twenty cards all tagged `food` have no valid arrangement, and a
 * deck dealt in a slightly clumpy order beats no deck at all.
 *
 * Index 0 is never moved: it is either the shuffle's own first card or the one
 * a link asked for, and re-seating it would undo the pin.
 */
function separateTags(out: IcebreakerCard[]): void {
  for (let round = 0; round < out.length; round++) {
    const clash = out.findIndex((c, i) => i > 0 && c.tag && c.tag === out[i - 1].tag);
    if (clash === -1) return;

    let fixed = false;
    for (let j = 1; j < out.length && !fixed; j++) {
      if (j === clash) continue;
      [out[clash], out[j]] = [out[j], out[clash]];
      if (settled(out, clash) && settled(out, j)) fixed = true;
      else [out[clash], out[j]] = [out[j], out[clash]];
    }
    if (!fixed) return;
  }
}

/**
 * The order the deck is dealt in.
 *
 * **Pure, and a function of the seed rather than of when it ran.** That is the
 * whole reason a seed exists instead of a plain `Math.random` shuffle held in
 * state: `useContent` serves cache first and then the network, so the card
 * array arrives twice on nearly every visit. A shuffle that re-ran on the
 * second arrival would reorder the deck under the guest's thumb — they would
 * swipe to card four and find a different card four. Same seed, same ids, same
 * deal, however many times it is recomputed.
 *
 * `first` pins the card a link named to the front, so arriving on
 * `/icebreakers/rename-an-animal` shows that card and shuffles the rest behind
 * it.
 */
export function dealOrder(
  cards: IcebreakerCard[],
  seed: number,
  first?: string,
): IcebreakerCard[] {
  const out = [...cards];
  const rand = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }

  if (first) {
    const at = out.findIndex((c) => c.id === first);
    if (at > 0) out.unshift(...out.splice(at, 1));
  }

  separateTags(out);
  return out;
}

export const newSeed = (): number => Math.floor(Math.random() * 2 ** 31);

/**
 * Read the seed, minting one on first use.
 *
 * In `sessionStorage` and not in state, so a reload — or moving between the
 * Schedule's copy of the deck and the route's — keeps the deal a guest was part
 * way through. Per tab, like the PKCE verifier and the deep-link breadcrumb
 * beside it: two people passing a phone around are not owed the same shuffle.
 *
 * Writing from inside a `useState` initialiser is safe here, and deliberately
 * so: React calls it twice in development, and the second call reads back what
 * the first wrote rather than minting a second seed.
 */
export function readSeed(): number {
  try {
    const raw = sessionStorage.getItem(SEED_KEY);
    const stored = raw === null ? NaN : Number(raw);
    if (Number.isFinite(stored)) return stored;
    const fresh = newSeed();
    sessionStorage.setItem(SEED_KEY, String(fresh));
    return fresh;
  } catch {
    // Private mode. A fixed seed deals one fixed order, which is a worse deck
    // than a random one and a much better screen than a crash.
    return 1;
  }
}

/** Persist a new seed, ignoring a storage that will not have it. */
export function writeSeed(seed: number): void {
  try {
    sessionStorage.setItem(SEED_KEY, String(seed));
  } catch {
    /* See `readSeed`. */
  }
}
